import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import axios from 'axios';
import { 
  Search, 
  Filter, 
  Download, 
  Calendar, 
  Users, 
  MessageSquare, 
  Bot, 
  Clock,
  ChevronDown,
  ChevronUp,
  Eye,
  BarChart3,
  RefreshCw,
  Zap,
  DollarSign,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const ChatLogExplorer = () => {
  const { getCurrentTenant } = useAuth();
  const { isDark } = useTheme();
  
  // State management
  const [chatLogs, setChatLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [usersWithChats, setUsersWithChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [error, setError] = useState('');

  // Helper function to get tenant information
  const getCurrentTenantInfo = () => {
    const currentTenant = getCurrentTenant();
    const tenantCode = currentTenant?.code || currentTenant?.tenant_code;
    const tenantName = currentTenant?.name || currentTenant?.tenant_name;
    
    // If no tenant code or it's DEFAULT, this indicates a problem
    if (!tenantCode || tenantCode === 'DEFAULT') {
      console.warn('🚨 Tenant context lost! Attempting to refresh user session...');
      // Force a page refresh to re-authenticate and get proper tenant context
      window.location.reload();
      return {
        code: 'LOADING',
        name: 'Loading Tenant...'
      };
    }
    
    return {
      code: tenantCode,
      name: tenantName
    };
  };
  
  // Filter and pagination state
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    aiAgent: '',
    channel: '',
    chatSource: '',
    searchTerm: '',
    page: 1,
    limit: 50,
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false
  });

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showStatistics, setShowStatistics] = useState(true);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch chat logs with filters
  const fetchChatLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${backendUrl}/api/ai/chat/admin/chat-logs?${params}`);
      
      if (response.data.message) {
        setChatLogs(response.data.data.conversations || []);
        setPagination(response.data.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: response.data.data.conversations?.length || 0,
          hasNext: false,
          hasPrevious: false
        });
      } else {
        setError('Failed to fetch chat logs');
      }
    } catch (error) {
      console.error('Error fetching chat logs:', error);
      setError(error.response?.data?.error || 'Failed to fetch chat logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${backendUrl}/api/ai/chat/admin/chat-statistics?${params}`);
      
      if (response.data.message) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setStatisticsLoading(false);
    }
  }, [filters.startDate, filters.endDate]);

  // Fetch users with chats
  const fetchUsersWithChats = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${backendUrl}/api/ai/chat/admin/users-with-chats`);
      
      if (response.data.message) {
        setUsersWithChats(response.data.data.users);
      }
    } catch (error) {
      console.error('Error fetching users with chats:', error);
    }
  }, []);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Export chat logs
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && key !== 'page' && key !== 'limit') {
          params.append(key, filters[key]);
        }
      });

      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${backendUrl}/api/ai/chat/admin/chat-export?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat_logs_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting chat logs:', error);
      setError('Failed to export chat logs');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      userId: '',
      startDate: '',
      endDate: '',
      aiAgent: '',
      searchTerm: '',
      page: 1,
      limit: 50,
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
  };

  // Quick date filters
  const setQuickDateFilter = (period) => {
    const now = new Date();
    let startDate = new Date();
    
    switch (period) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case '3days':
        startDate.setDate(now.getDate() - 3);
        break;
      case '7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(now.getDate() - 30);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      page: 1
    }));
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId) => {
    setDeleting(true);
    setError(''); // Clear any previous errors
    try {
      console.log('Attempting to delete conversation:', conversationId);
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.delete(`${backendUrl}/api/ai/chat/admin/chat-logs/${conversationId}`);
      
      console.log('Delete response:', response.data);
      
      if (response.data.success || response.data.message) {
        // Remove from local state
        setChatLogs(prev => prev.filter(chat => chat.id !== conversationId));
        setPagination(prev => ({ ...prev, totalCount: prev.totalCount - 1 }));
        setDeleteConfirmation(null);
        
        // Refresh statistics
        fetchStatistics();
        
        // Show success message (optional - you can remove this if you don't want a success message)
        console.log('Conversation deleted successfully');
      } else {
        setError('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || error.response?.data?.details || 'Failed to delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    fetchChatLogs();
  }, [fetchChatLogs]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  useEffect(() => {
    fetchUsersWithChats();
  }, [fetchUsersWithChats]);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format message length for display
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format token numbers for display
  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens < 1000) return tokens.toString();
    if (tokens < 1000000) return (tokens / 1000).toFixed(1) + 'K';
    return (tokens / 1000000).toFixed(1) + 'M';
  };

  // Format cost for display
  const formatCost = (cost) => {
    if (!cost) return '$0.0000';
    const numCost = parseFloat(cost);
    if (numCost < 0.01) return `$${numCost.toFixed(4)}`;
    return `$${numCost.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Analyze and monitor AI SalesBuddy conversations for {getCurrentTenantInfo().name} ({getCurrentTenantInfo().code})
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            {showStatistics ? 'Hide' : 'Show'} Stats
          </button>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => {
              fetchChatLogs();
              fetchStatistics();
              fetchUsersWithChats();
            }}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {showStatistics && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Total Users</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{statistics.totalUsers}</p>
                </div>
              </div>
            </div>
            
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <MessageSquare className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Total Interactions</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{statistics.totalInteractions}</p>
                </div>
              </div>
            </div>
            
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <Bot className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Messages</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{statistics.totalMessages}</p>
                </div>
              </div>
            </div>
            
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Avg per User</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{statistics.averageInteractionsPerUser}</p>
                </div>
              </div>
            </div>

            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <Zap className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Total Tokens</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatTokens(statistics.tokenUsage?.total_tokens)}
                  </p>
                </div>
              </div>
            </div>

            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-sm`}>
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Est. Cost</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatCost(statistics.tokenUsage?.cost_estimates && 
                      Object.values(statistics.tokenUsage.cost_estimates)
                        .reduce((sum, cost) => sum + parseFloat(cost.total_cost || 0), 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Token Usage Breakdown */}
      {showStatistics && statistics && statistics.tokenUsage && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm mb-6`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-medium flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Zap className="w-5 h-5 mr-2" />
                Token Usage Breakdown
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Token Usage by Agent */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>By AI Agent</h3>
                  <div className="space-y-2">
                    {Object.entries(statistics.tokenUsage.by_agent || {}).map(([agent, usage]) => (
                      <div key={agent} className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <span className={`font-medium ${
                          agent === 'openai' ? 'text-green-600' :
                          agent === 'gemini' ? 'text-blue-600' : 'text-purple-600'
                        }`}>
                          {agent.toUpperCase()}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {formatTokens(usage.total_tokens)} tokens
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cost Estimates */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Cost Estimates</h3>
                  <div className="space-y-2">
                    {Object.entries(statistics.tokenUsage.cost_estimates || {}).map(([agent, cost]) => (
                      <div key={agent} className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <span className={`font-medium ${
                          agent === 'openai' ? 'text-green-600' :
                          agent === 'gemini' ? 'text-blue-600' : 'text-purple-600'
                        }`}>
                          {agent.toUpperCase()}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {formatCost(cost.total_cost)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Token Averages */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Averages</h3>
                  <div className="space-y-2">
                    <div className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Per Interaction</span>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {statistics.tokenUsage.average_per_interaction || 0} tokens
                      </span>
                    </div>
                    <div className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Prompt/Completion</span>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {((statistics.tokenUsage.prompt_tokens || 0) / (statistics.tokenUsage.completion_tokens || 1)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Filters */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm mb-6`}>
          <div 
            className={`px-6 py-4 border-b cursor-pointer flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <h2 className={`text-lg font-medium flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </h2>
            {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
          
          {showFilters && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* User Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    User
                  </label>
                  <select
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="">All Users</option>
                    {usersWithChats.map(user => (
                      <option key={user.userId} value={user.userId}>
                        User {user.userId} ({user.fileCount} files)
                      </option>
                    ))}
                  </select>
                </div>

                {/* AI Agent Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    AI Agent
                  </label>
                  <select
                    value={filters.aiAgent}
                    onChange={(e) => handleFilterChange('aiAgent', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="">All Agents</option>
                    <option value="openai">OpenAI</option>
                    <option value="gemini">Gemini</option>
                    <option value="openrouter">OpenRouter</option>
                  </select>
                </div>

                {/* Channel Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Channel
                  </label>
                  <select
                    value={filters.channel}
                    onChange={(e) => handleFilterChange('channel', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="">All Channels</option>
                    <option value="web">Web</option>
                    <option value="line">Line</option>
                  </select>
                </div>

                {/* Chat Source Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Chat Source
                  </label>
                  <select
                    value={filters.chatSource}
                    onChange={(e) => handleFilterChange('chatSource', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="">All Sources</option>
                    <option value="salesassist">salesassist</option>
                    <option value="power_sales">power_sales (legacy)</option>
                    <option value="salesbuddy">salesbuddy</option>
                    <option value="power_assistant">power_assistant (legacy)</option>
                    <option value="web">web</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {/* Quick Date Filters */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Quick Date Filters
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setQuickDateFilter('today')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isDark 
                        ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('3days')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isDark 
                        ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Last 3 Days
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('7days')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isDark 
                        ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('30days')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isDark 
                        ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Last Month
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Search in Messages
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    placeholder="Search in user messages and AI responses..."
                    className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={clearFilters}
                  className={`px-4 py-2 border rounded-md transition-colors ${
                    isDark 
                      ? 'text-gray-300 border-gray-600 hover:bg-gray-700' 
                      : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Clear Filters
                </button>
                <button
                  onClick={() => handleFilterChange('page', 1)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Error Message */}
      {error && (
          <div className={`border rounded-md p-4 mb-6 ${
            isDark 
              ? 'bg-red-900/20 border-red-700' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="text-red-700">{error}</div>
          </div>
        )}

      {/* Chat Logs Table */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Chat Conversations ({pagination.totalCount} total)
              </h2>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider w-48 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    User & Time
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    User Message
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    AI Response
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider w-40 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Agent & Tokens
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider w-40 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="animate-spin w-6 h-6 text-blue-600 mr-2" />
                        Loading chat logs...
                      </div>
                    </td>
                  </tr>
                ) : chatLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No chat logs found with current filters
                    </td>
                  </tr>
                ) : (
                  chatLogs.map((conversation) => (
                    <tr key={conversation.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>User {conversation.userId}</div>
                          <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>{formatDate(conversation.timestamp)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="truncate">{conversation.userMessage}</div>
                          <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {conversation.messageLength.user} chars
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="truncate">{conversation.aiResponse}</div>
                          <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {conversation.messageLength.ai} chars
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mb-1 ${
                            conversation.aiAgent === 'openai' ? (isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800') :
                            conversation.aiAgent === 'gemini' ? (isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800') :
                            (isDark ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800')
                          }`}>
                            {conversation.aiAgent}
                          </span>
                          {conversation.metadata?.tokenUsage && (
                            <div className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <div className="flex items-center">
                                <Zap className="w-3 h-3 mr-1" />
                                {formatTokens(conversation.metadata.tokenUsage.total_tokens)} tokens
                              </div>
                              <div className="text-xs">
                                P: {conversation.metadata.tokenUsage.prompt_tokens || 0} | 
                                C: {conversation.metadata.tokenUsage.completion_tokens || 0}
                              </div>
                            </div>
                          )}
                          {/* Channel & Source badges */}
                          {(conversation.metadata?.channel || conversation.metadata?.lineIntegration || conversation.metadata?.chatSource || conversation.metadata?.agentType) && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {(conversation.metadata?.channel || conversation.metadata?.lineIntegration) && (
                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                  (conversation.metadata?.channel || (conversation.metadata?.lineIntegration ? 'line' : '')) === 'line'
                                    ? (isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700')
                                    : (isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                                }`}>
                                  {(conversation.metadata?.channel || (conversation.metadata?.lineIntegration ? 'line' : 'web')).toUpperCase()}
                                </span>
                              )}
                              {(conversation.metadata?.chatSource || conversation.metadata?.agentType) && (
                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                  {(conversation.metadata?.chatSource || conversation.metadata?.agentType)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => setSelectedConversation(conversation)}
                            className={`flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              isDark 
                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            <Eye className="w-3 h-3 mr-1.5" />
                            View Details
                          </button>
                          <button
                            onClick={() => setDeleteConfirmation(conversation)}
                            className={`flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              isDark 
                                ? 'bg-red-600 text-white hover:bg-red-700' 
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            <Trash2 className="w-3 h-3 mr-1.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className={`px-6 py-4 border-t flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
                {Math.min(pagination.currentPage * filters.limit, pagination.totalCount)} of{' '}
                {pagination.totalCount} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevious}
                  className={`px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark 
                      ? 'border-gray-600 hover:bg-gray-700' 
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md">
                  {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className={`px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark 
                      ? 'border-gray-600 hover:bg-gray-700' 
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Conversation Detail Modal */}
      {selectedConversation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden`}>
              <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Conversation Details
                </h3>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className={`grid grid-cols-2 gap-4 mb-6 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                  <div>
                    <strong>User ID:</strong> {selectedConversation.userId}
                  </div>
                  <div>
                    <strong>Timestamp:</strong> {formatDate(selectedConversation.timestamp)}
                  </div>
                  <div>
                    <strong>AI Agent:</strong> {selectedConversation.aiAgent}
                  </div>
                  <div>
                    <strong>Model:</strong> {selectedConversation.metadata?.model || selectedConversation.aiAgent}
                  </div>
                  <div>
                    <strong>Response Time:</strong> {selectedConversation.metadata?.responseTime || 'N/A'} ms
                  </div>
                  <div>
                    <strong>Interaction ID:</strong> {selectedConversation.id}
                  </div>
                </div>

                {/* Token Usage Section */}
                {selectedConversation.metadata?.tokenUsage && (
                  <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-3 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <Zap className="w-4 h-4 mr-2" />
                      Token Usage
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>Prompt Tokens</div>
                        <div className="font-medium text-blue-600">
                          {selectedConversation.metadata.tokenUsage.prompt_tokens || 0}
                        </div>
                      </div>
                      <div>
                        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>Completion Tokens</div>
                        <div className="font-medium text-green-600">
                          {selectedConversation.metadata.tokenUsage.completion_tokens || 0}
                        </div>
                      </div>
                      <div>
                        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>Total Tokens</div>
                        <div className="font-medium text-purple-600">
                          {selectedConversation.metadata.tokenUsage.total_tokens || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>User Message:</h4>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                      <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedConversation.userMessage}</p>
                      <div className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedConversation.messageLength.user} characters
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>AI Response:</h4>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedConversation.aiResponse}</p>
                      <div className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedConversation.messageLength.ai} characters
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-md w-full`}>
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-medium flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  Confirm Delete
                </h3>
              </div>
              
              <div className="p-6">
                <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Are you sure you want to delete this conversation? This action cannot be undone.
                </p>
                
                <div className={`p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="text-sm">
                    <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>User ID: {deleteConfirmation.userId}</div>
                    <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>{formatDate(deleteConfirmation.timestamp)}</div>
                    <div className={`mt-2 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      "{deleteConfirmation.userMessage.substring(0, 100)}..."
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setDeleteConfirmation(null)}
                    disabled={deleting}
                    className={`px-4 py-2 border rounded-md transition-colors disabled:opacity-50 ${
                      isDark 
                        ? 'text-gray-300 border-gray-600 hover:bg-gray-700' 
                        : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteConversation(deleteConfirmation.id)}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {deleting ? (
                      <>
                        <RefreshCw className="animate-spin w-4 h-4 mr-2" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ChatLogExplorer;